import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Diagram,
  ModelConceptRelationshipLink,
} from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { isLink } from 'src/containers/ProjectDiagram/utils/graphObject';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addModelConceptRelationship';
import { changeModelConceptRelationshipConnection } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/changeModelConceptRelationshipConnection';
import {
  addProjectModelIdsToIri,
  getProjectModelIdsForIri,
} from 'src/containers/ProjectDiagram/utils/projectModelIds';

export const addDomainOrRangeToRelationship =
  ({
    direction,
    relationshipIri,
    classIri,
    copyModelIds = false,
    useAllValidLinks = false,
  }: {
    classIri: string;
    direction: RelationshipDirection;
    relationshipIri: string;
    copyModelIds?: boolean;
    useAllValidLinks?: boolean;
  }) =>
  (model: Model, diagram?: Diagram) => {
    const isOutgoing = direction === RelationshipDirection.OUTGOING;
    const isDanglingEnd = (data: ModelConceptRelationship) =>
      data.value === relationshipIri && (isOutgoing ? !data.from : !data.to);

    // `useAllValidLinks` scans every relationship link, which the model holds in
    // full — no diagram needed. The `diagram.selection` branch is a canvas-only
    // affordance (reconnecting a link the user selected) with no headless
    // equivalent, so it yields nothing without a diagram.
    let validLinkData: ModelConceptRelationship[];
    if (useAllValidLinks) {
      validLinkData = model.linkDataArray.filter(
        (data): data is ModelConceptRelationship =>
          isModelConceptRelationship(data) && isDanglingEnd(data)
      );
    } else if (diagram) {
      validLinkData = diagram.selection
        .toArray()
        .filter((part) =>
          isLink<ModelConceptRelationshipLink>(
            part,
            LinkCategory.MODEL_CONCEPT_RELATIONSHIP
          )
        )
        .map((part) => (part as ModelConceptRelationshipLink).data)
        .filter(isDanglingEnd);
    } else {
      validLinkData = [];
    }

    const reconnectedRelationships: ModelConceptRelationship[] = [];
    const reconnectedRelationshipLabels: ModelConceptRelationshipLabel[] = [];
    validLinkData.forEach((data) => {
      // Mirror the live-parts logic: the reconnected end becomes classIri, the
      // opposite end keeps whatever class it already had (undefined if detached,
      // which stays detached).
      const { linkData, linkLabelData } =
        changeModelConceptRelationshipConnection({
          key: data.key,
          fromIri: isOutgoing
            ? classIri
            : data.from && getModelConceptClassValue(data.from),
          toIri: isOutgoing
            ? data.to && getModelConceptClassValue(data.to)
            : classIri,
        })(model);
      if (linkData) {
        reconnectedRelationships.push(linkData);
      }
      if (linkLabelData) {
        reconnectedRelationshipLabels.push(linkLabelData);
      }
    });

    const { addedRelationships, addedRelationshipLabels } =
      addModelConceptRelationship({
        direction,
        iri: relationshipIri,
        classIri,
        addUnconnectedRelationship: !validLinkData.length,
      })(model, diagram);

    if (copyModelIds) {
      addProjectModelIdsToIri(
        relationshipIri,
        getProjectModelIdsForIri(classIri, model)
      )(model);
    }

    return {
      addedRelationships,
      addedRelationshipLabels,
      reconnectedRelationships,
      reconnectedRelationshipLabels,
    };
  };
